/**
 * @deprecated use BaseMap and layer builders instead
 */

import "src/js/util/map_loader";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Feature, SiteLayer } from "../layers/site/SiteLayer";
import { GoogleMapsOverlay } from "@deck.gl/google-maps";
import { Layer } from "@deck.gl/core";

export type PlanningApplicationMapOptions = {
  planningApplicationIds: string[];
};

interface MapProps {
  bounds?: google.maps.LatLngBounds;
  siteLatLng?: {
    lat: number;
    lng: number;
  };
  siteFeatures?: Feature[];
  siteColor?: string;
  interactive?: boolean;
  initialMapTypeId?: string;
  mapZoom?: number;
  onMapLoaded?: (isLoaded: boolean) => void;
  layers?: Layer<any>[];
}

export const Map: FC<MapProps> = ({
  bounds,
  siteLatLng,
  siteFeatures,
  siteColor,
  interactive,
  initialMapTypeId,
  mapZoom,
  onMapLoaded = () => false,
  layers,
}) => {
  const mapElRef = useRef<HTMLDivElement>(null);
  const [googleMapInstance, setGoogleMapInstance] = useState<google.maps.Map<HTMLDivElement>>();

  const deckLayers = useMemo(() => {
    const response = layers ?? [];

    if (siteFeatures && siteColor) {
      response.push(
        new SiteLayer({
          features: siteFeatures,
          hexColor: siteColor,
        })
      );
    }

    return response;
  }, [layers, siteColor, siteFeatures]);

  useEffect(() => {
    if (!mapElRef.current) return;
    if (!bounds) return;
    if (!siteLatLng) return;

    const googleMap = new window.google.maps.Map(mapElRef.current, {
      tilt: 0,
      heading: 0,
      zoom: mapZoom,
      mapTypeControl: interactive,
      mapTypeId: initialMapTypeId ?? "satellite",
      mapTypeControlOptions: {
        mapTypeIds: ["roadmap", "satellite"],
        style: google.maps.MapTypeControlStyle.DEFAULT,
        position: google.maps.ControlPosition.LEFT_BOTTOM,
      },
      fullscreenControl: false,
      controlSize: 25,
      maxZoom: 20,
      minZoom: 7,
      zoomControl: interactive,
      draggable: interactive,
      zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM },
      streetViewControl: interactive,
      streetViewControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM },
      rotateControl: interactive,
      rotateControlOptions: { position: google.maps.ControlPosition.TOP_LEFT },
      gestureHandling: "greedy" as const,
      scaleControl: interactive,
      styles: [
        {
          featureType: "poi" as const,
          stylers: [{ visibility: "on" }],
        },
      ],
    });

    // For some constraints they are national e.g. flood zones so we can't fit bounds.
    // Just set a sensible zoom level enough for the user to get the context.
    if (mapZoom) {
      mapZoom && googleMap.setCenter(siteLatLng);
      mapZoom && googleMap.setZoom(mapZoom);
    } else {
      googleMap.fitBounds(bounds);
      googleMap.panToBounds(bounds);
    }

    interactive &&
      new google.maps.Marker({
        position: siteLatLng,
        map: googleMap,
      });

    googleMap.addListener("tilesloaded", () => onMapLoaded(true));

    setGoogleMapInstance(googleMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bounds, mapZoom]);

  useEffect(() => {
    if (googleMapInstance) {
      const deckInstance = new GoogleMapsOverlay({
        style: { zIndex: 100 },
        layers: deckLayers,
      });

      deckInstance.setMap(googleMapInstance);

      return () => {
        deckInstance.finalize();
      };
    }
  }, [googleMapInstance, deckLayers]);

  return <div className="atlas-h-full atlas-rounded-sm" ref={mapElRef} />;
};
